/**
 * LineChart Component
 * Reusable line chart with customizable dots and stroke
 */

"use client";

import {
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  CartesianGrid,
  Line,
  LineChart as RechartsLineChart,
  XAxis,
  YAxis,
} from "recharts";
import { ChartWrapper } from "./ChartWrapper";
import { BaseChartData, LineChartProps } from "./types";
import { formatAxisTick } from "./utils";

export const LineChart = ({
  data,
  config,
  dataKeys,
  xAxisKey,
  title,
  description,
  footer,
  className,
  containerClassName,
  height = 350,
  curveType = "monotone",
  showGrid = true,
  showXAxis = true,
  showYAxis = true,
  showTooltip = true,
  showLegend = false,
  tooltipConfig,
  legendConfig,
  strokeWidth = 2,
  showDots = true,
  dotSize = 4,
  activeDotSize = 6,
  accessibilityLayer = true,
  metricValue,
  metricChange,
  showMetricLayout = false,
  headerActions,
}: LineChartProps) => {
  // Wrapper functions for formatAxisTick to match Recharts signature
  const formatXAxisTick = (value: any) => formatAxisTick(value);
  const formatYAxisTick = (value: any) => formatAxisTick(value, true);

  const chartContent = (
    <RechartsLineChart
      accessibilityLayer={accessibilityLayer}
      data={data}
      margin={{ top: 10, right: 12, left: -20, bottom: 0 }}
    >
      {showGrid && <CartesianGrid vertical={false} />}

      {showXAxis && (
        <XAxis
          dataKey={xAxisKey}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={formatXAxisTick}
        />
      )}

      {showYAxis && (
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={formatYAxisTick}
        />
      )}

      {showTooltip && (
        <ChartTooltip
          cursor={tooltipConfig?.cursor ?? false}
          content={
            <ChartTooltipContent
              indicator={tooltipConfig?.indicator ?? "line"}
              hideLabel={tooltipConfig?.hideLabel}
              nameKey={tooltipConfig?.nameKey}
            />
          }
          defaultIndex={tooltipConfig?.defaultIndex}
        />
      )}

      {showLegend && (
        <ChartLegend
          content={<ChartLegendContent />}
          className="-translate-y-2"
        />
      )}

      {dataKeys.map((key: keyof BaseChartData, index: number) => {
        const configKey = String(key);

        // Use color from config, fallback to dynamic chart color if not defined
        const configColor = config[configKey]?.color;
        const fallbackChartNumber = (index % 5) + 1;
        const chartVariable =
          configColor || `var(--chart-${fallbackChartNumber})`;

        return (
          <Line
            key={configKey}
            type={curveType}
            dataKey={key}
            stroke={chartVariable}
            strokeWidth={strokeWidth}
            dot={showDots ? { r: dotSize } : false}
            activeDot={{ r: activeDotSize }}
          />
        );
      })}
    </RechartsLineChart>
  );

  return (
    <ChartWrapper
      title={title}
      description={description}
      footer={footer}
      className={className}
      containerClassName={containerClassName}
      config={config}
      height={height}
      metricValue={metricValue}
      metricChange={metricChange}
      showMetricLayout={showMetricLayout}
      headerActions={headerActions}
    >
      {chartContent}
    </ChartWrapper>
  );
};

LineChart.displayName = "LineChart";

export default LineChart;
